import {Injectable} from '@angular/core';
import {SocketMessagesService} from '../../core/socket/socket.service';
import {EMPTY, forkJoin, Observable, of} from 'rxjs';
import {catchError, map, pluck, shareReplay, switchMap, tap} from 'rxjs/operators';
import {
    IAIMLegalEntity,
    IAudioInput,
    IAudioInputParam,
    IBillingEntity,
    ICaptionOutputParam,
    ICaptionSource,
    IConfig,
    IConfigValue,
    IOrganization,
    IOrganizationExtended
} from '../../types';
import {CustomErrorHandlerService} from '../../core/custom-error-handler.service';

export interface IRegionalProductPrice {
    sku_regional_price_id?: string;
    product_id?: number;
    region_id: number; // ID of region that this price applies to
    currency: string; // currency of pricing
    hourly_rate: number; // float rate based on duration of session, per     hour (can be 0.00)
    fixed_price: number; // float fixed price in addition to hourly_rate     (can be 0.00)
    min_minutes: number; // – integer, if the duration of the session is less     than this value, this value will be used for pricing
    billing_increment_mins: number; // – integer, allows billing to take     place in rounded-up increments. Eg, if set to 15, and the     billing length is 65 minutes, pricing will round up to 75     minutes.
}

export interface ICustomRegionalProductPrice extends IRegionalProductPrice {
    region?: string;
}

export interface IBillingEntityProductPrice {
    sku_billing_entity_price_id?: number;
    product_id?: number;
    billing_entity_id: number;
    currency: string;
    hourly_rate: number; // float rate based on duration of session, per     hour (can be 0.00)
    fixed_price: number; // float fixed price in addition to hourly_rate     (can be 0.00)
    min_minutes: number; // – integer, if the duration of the session is less     than this value, this value will be used for pricing
    billing_increment_mins: number; // – integer, allows billing to take     place in rounded-up increments. Eg, if set to 15, and the     billing length is 65 minutes, pricing will round up to 75     minutes.
}

export interface IListBillingEntityProductPrice extends IBillingEntityProductPrice {
    billing_entity?: string;
}


export interface IGroup {
    group_id?: number;
    group_name: string;
    admin_group?: boolean;
    sys_admin_group?: boolean;
    ops_group?: boolean;
    create_user_in_org?: boolean;
    create_user_in_be?: boolean;
    create_user_in_any_org?: boolean;
    alter_org_group_permissions?: boolean;
    alter_be_group_permissions?: boolean;
    alter_any_group_permissions?: boolean;
    add_remove_users_from_this_group?: boolean;
    add_remove_users_from_be_group?: boolean;
    add_remove_users_from_org_group?: boolean;
    add_remove_users_from_any_group?: boolean;
    add_remove_group_in_any_org: boolean;
    create_booking_in_org: boolean;
    create_booking_in_be?: boolean;
    create_booking_in_any_org?: boolean;
    override_any_session_rate?: boolean;
    is_default: boolean;
    read_only_user: boolean;
    users?: IShortUser[];
    deleted?: number;
    undelete?: boolean;
    is_protected?: boolean;
}

export interface IApiToken {
    id: number;
    created_at: string;
    token?: string;
    type: string;
    status: number;
}

export interface IBookingDefaults {
    notes: string;
    private_link_type: 'anyone' | 'restricted';
    allow_transcript: number;
    caption: number;
    audio: number;
    audio_caption_details: number
    calendar_start_day: number;
    /** Preferred billing entity for booking defaults; when unset, clients use current billing entity id. */
    account_default?: number;
    point_of_contact: string;
    allow_confirmed_emails: number;
    allow_canceled_emails: number;
    allow_received_emails: number;
    ai_live_caption_setting: {
        font: string
        size: string
        colourcombination: string
        oneword: string
        spacing?: string
    };
}
export interface IUser {
    user_id?: number; // ID of user
    billing_entity_id: number; // billing entity ID that the user is attached to
    id: string; // login ID for the user (can be an email address)
    email: string; // email of the user
    phone_number?: string; // phone of the user
    first_name: string; // first name of user
    last_name: string; // last name of user
    time_zone_override?: string; // standard TZ database time zone name. If not present, assumes user is  the same time zone as billing entity
    federation_link?: string; // code used to link user to a federated system
    sync_calendar?: number;
    be_name?: string;
    curr_org_id?: number;
    org_id?: number;
    time_zone?: string;
    curr_billing_entity_id?: number;
    api_token?: IApiToken;
    developer?: number;
    booking_defaults?: IBookingDefaults;
    deleted?: number;
    undelete?: boolean;
}

export interface IGroupUsers {
    group_id: number;
    users: IShortUser[];
}

export interface IShortUser {
    user_id: number;
    first_name: string; // first name of user
    last_name: string; // last name of user
    email: string;
    be_name?: string;
}

export interface IUpdateBillingEntity extends Omit<IBillingEntity, 'hide_billing_reference'> {
    hide_billing_reference: boolean;
}


export type ICreateBillingEntity = Omit<IUpdateBillingEntity, 'billing_entity_id'>;

export interface IRegion {
    region_id: number;
    region_name: string;
    time_zone: string;
    at_base: string;
    currency: string;
}

export interface IRegionSupportUser {
    id?: number;
    region_id: number | null;
    user_id: number;
    added_by_user_id: number;
    created_epoch: number;
}

export type SupportGroupType = 'admin' | 'sys_admin' | 'ops' | 'read_only';

export interface ISupportGroupSearchRow {
    billing_entity_id: number;
    billing_entity_name: string;
    org_id: number;
    group_id: number;
    group_name: string;
    member_count: number;
    ai_media_domain_ratio: number | null;
    is_legacy_group: boolean;
    roster_covered: boolean;
    admin_group: number;
    sys_admin_group: number;
    ops_group: number;
    read_only_user: number;
    is_protected: boolean;
    is_default: number;
    create_booking_in_org: number;
    create_booking_in_be: number;
    create_booking_in_any_org: number;
}

export interface ISupportGroupDeleteTarget {
    billing_entity_id: number;
    group_id: number;
}

export type ISupportGroupDeleteOutcome = 'deleted' | 'skipped_target_not_found' | 'skipped_protected' | 'failed';

export interface ISupportGroupDeleteResult extends ISupportGroupDeleteTarget {
    outcome: ISupportGroupDeleteOutcome;
    reason?: string;
}

export interface IProduct {
    product_id?: number;
    sku: string;
    description: string;
    add_on: boolean;
    work_zone_id: number;
}

export interface IProductParam {
    product_param_id?: number;
    product_id?: number;
    param_name: string;
    description: string;
}

export interface IProductParamValue {
    param_value_id?: number;
    product_param_id: number;
    order_num: number;
    value: string;
    hint?: string;
}

@Injectable({
    providedIn: 'root'
})
export class SettingsService {
    private regions$?: Observable<IRegion[]>;
    public org_has_knowledge_base!: number;

    constructor(
        private messages: SocketMessagesService,
        private errors: CustomErrorHandlerService
    ) {
    }

    getRegions$(): Observable<IRegion[]> {
        if (!this.regions$) {
            this.regions$ = this.request$<{ regions: IRegion[] }>('getRegions').pipe(
                pluck('regions'),
                shareReplay(1),
            );
        }
        return this.regions$;
    }

    createAudioInput$(name: string): Observable<unknown> {
        return this.request$('createAudioInput', {audio_name: name});
    }

    updateAudioInput$(data: IAudioInput): Observable<unknown> {
        return this.request$('updateAudioInput', data);
    }

    delAudioInput$(id: number): Observable<unknown> {
        return this.request$('delAudioInput', {audio_input_id: id});
    }

    addAudioInputParam$(audio_input_id: number, data: IAudioInputParam): Observable<unknown> {
        return this.request$('addAudioInputParam', Object.assign(data, {audio_input_id})).pipe(

        );
    }

    delAudioInputParam$(audio_param_id: number): Observable<unknown> {
        return this.request$('delAudioInputParam', {audio_param_id}).pipe(

        );
    }


    createCaptionOutput$(name: string): Observable<unknown> {
        return this.request$<{ caption_outputs: ICaptionSource[] }>('createCaptionOutput', {caption_output_name: name});
    }

    updateCaptionOutput$(captionOutput: ICaptionSource): Observable<unknown> {
        return this.request$('updateCaptionOutput', captionOutput);
    }

    delCaptionOutput$(id: number): Observable<unknown> {
        return this.request$('delCaptionOutput', {caption_output_id: id});
    }

    addCaptionOutputParam$(caption_output_id: number, data: ICaptionOutputParam): Observable<unknown> {
        return this.request$('addCaptionOutputParam', Object.assign(data, {caption_output_id})).pipe(
        );
    }

    delCaptionOutputParam$(caption_param_id: number) {
        return this.request$('delCaptionOutputParam', {caption_param_id}).pipe(

        );
    }

    getBillingEntities$(org_id?: number): Observable<IBillingEntity[]> {
        return this.request$<{ billing_entities: IBillingEntity[] }>('getBillingEntities', {org_id}).pipe(
            pluck('billing_entities')
        );
    }

    createBillingEntity$(data: ICreateBillingEntity): Observable<number> {
        return this.request$<{ billing_entity: number }>(
            'createBillingEntity',
            Object.assign({}, data,
                {
                    hide_billing_reference: data.hide_billing_reference ? 1 : 0,
                    hide_billing_contact: data.hide_billing_contact ? 1 : 0,
                    hide_consumer_dropdown: data.hide_consumer_dropdown ? 1 : 0,
                    hide_private_viewer_dropdown: data.hide_private_viewer_dropdown ? 1 : 0,
                    hide_mlt: data.hide_mlt ? 1 : 0,
                    hide_consumers_manager_view: data.hide_consumers_manager_view ? 1 : 0
                }
            )).pipe(
                pluck('billing_entity')
            );
    }

    updateBillingEntity$(data: IUpdateBillingEntity): Observable<unknown> {
        return this.request$(
            'updateBillingEntity',
            Object.assign({}, data,
                {
                    hide_billing_reference: data.hide_billing_reference ? 1 : 0,
                    hide_billing_contact: data.hide_billing_contact ? 1 : 0,
                    hide_consumer_dropdown: data.hide_consumer_dropdown ? 1 : 0,
                    hide_private_viewer_dropdown: data.hide_private_viewer_dropdown ? 1 : 0,
                    hide_mlt: data.hide_mlt ? 1 : 0,
                    hide_consumers_manager_view: data.hide_consumers_manager_view ? 1 : 0
                }
            )
        );
    }

    delBillingEntity$(id: number): Observable<unknown> {
        return this.request$('delBillingEntity', {billing_entity_id: id});
    }

    getOrganizations$(org_id?: number): Observable<IOrganization[]> {
        let data;
        if (org_id) {
            data = {org_id};
        }

        const x = this.request$<{ organisations: IOrganization[] }>('getOrganisations', data).pipe(
            tap((data: any) => {
                this.org_has_knowledge_base = data.organisations[0]?.has_knowledge_base;
            }),
            pluck('organisations'));
        return  x;
    }

    createOrganization$(data: IOrganizationExtended): Observable<unknown> {
        const {
            organization: {
                org_name,
                org_caption_overrun,
                guaranteed_period,
                show_pricing_estimate,
                theme,
                hd,
                restrict_language,
                multilingual,
                white_label,
                disclaimer_acv,
                recall_ai,
                recall_ai_msg,
                allow_transcripts,
                allow_transcript_tools,
                transcript_retention,
                show_banner,
                banner_message
            },
            audio_sources,
            display_captions
        } = data;

        return this.request$<{ org_id: number }>('createOrganisation', {
            org_name,
            org_caption_overrun,
            guaranteed_period,
            theme,
            hd,
            show_pricing_estimate: show_pricing_estimate ? 1 : 0,
            multilingual: multilingual ? 1 : 0,
            white_label: white_label ? 1 : 0,
            disclaimer_acv: disclaimer_acv ? 1 : 0,
            recall_ai: recall_ai ? 1 : 0,
            recall_ai_msg,
            restrict_language: restrict_language ? 1 : 0,
            allow_transcripts: allow_transcripts ? 1 : 0,
            allow_transcript_tools: allow_transcript_tools ? 1 : 0,
            transcript_retention: transcript_retention,
            show_banner: show_banner ? 1 : 0,
            banner_message
        }).pipe(
            switchMap(({org_id}) => {
                const requests = [
                    this.setOrganizationAudio(org_id, audio_sources),
                    this.setOrganizationCaptions(org_id, display_captions || [])
                ];

                return forkJoin(requests);
            }),
        );
    }

    mergeOrganizations$(merge_from: number[], merge_to: number): Observable<unknown> {
        return this.request$('mergeOrganisations', {merge_from, merge_to});
    }

    updateOrganization$(data: IOrganizationExtended): Observable<unknown> {
        const {
            organization: {
                org_id,
                org_name,
                org_caption_overrun,
                guaranteed_period,
                show_pricing_estimate,
                theme,
                hd,
                multilingual,
                white_label,
                disclaimer_acv,
                recall_ai,
                recall_ai_msg,
                restrict_language,
                allow_transcripts,
                allow_transcript_tools,
                transcript_retention,
                show_banner,
                banner_message,
                settings
            },
            audio_sources,
            display_captions
        } = data;

        return this.request$('updateOrganisation', {
            org_id,
            org_name,
            org_caption_overrun,
            guaranteed_period,
            theme,
            hd: hd || '',
            multilingual: multilingual ? 1 : 0,
            white_label: white_label ? 1 : 0,
            disclaimer_acv: disclaimer_acv ? 1 : 0,
            recall_ai: recall_ai ? 1 : 0,
            recall_ai_msg,
            restrict_language: restrict_language ? 1 : 0,
            show_pricing_estimate: show_pricing_estimate ? 1 : 0,
            allow_transcripts: allow_transcripts ? 1 : 0,
            allow_transcript_tools: allow_transcript_tools ? 1 : 0,
            transcript_retention: transcript_retention,
            show_banner: show_banner ? 1 : 0,
            banner_message: banner_message,
            settings
        }).pipe(
            switchMap(() => {
                return this.setOrganizationAudio(org_id as number, audio_sources);
            }),
            switchMap(() => {
                return this.setOrganizationCaptions(org_id as number, display_captions as number[]);
            })
        );
    }

    delOrganization$(org_id: number): Observable<unknown> {
        return this.request$('delOrganisation', {org_id});
    }

    private setOrganizationAudio(org_id: number, audio_input_ids: number[]): Observable<unknown> {
        return this.request$('setOrganisationAudio', {org_id, audio_input_ids});
    }

    private setOrganizationCaptions(org_id: number, caption_output_ids: number[]): Observable<unknown> {
        return this.request$('setOrganisationCaptions', {org_id, caption_output_ids});
    }

    getUsers$(billing_entity_id: number): Observable<IUser[]> {
        return this.request$<{ users: IUser[] }>('getUsers', {billing_entity_id}).pipe(
            pluck('users')
        );

    }

    getOrgUsers$(data: { billing_entity_id: number; org_id: number }): Observable<IUser[]> {
        return this.request$<{ users: IUser[] }>('getOrgUsers', data).pipe(
            pluck('users')
        );

    }

    updateUser$(data: IUser): Observable<unknown> {
        return this.request$('updateUser', data).pipe(

        );
    }

    createUser$(data: IUser): Observable<unknown> {
        return this.request$('createUser', data).pipe(
        );
    }

    delUser$(user_id: number): Observable<unknown> {
        return this.request$('delUser', {user_id}).pipe(

        );
    }

    getGroups$(org_id: number, billing_entity_id: number): Observable<IGroup[]> {
        const nonConvertedGroups = [
            'group_id',
            'group_name',

            'org_id',
            'billing_entity_id'
        ];
        return this.request$<{ groups: unknown[] }>('getGroups', {org_id, billing_entity_id}).pipe(
            pluck('groups'),
            map((groups: unknown[]) => {
                return groups.map((group) => {
                    Object.keys(group as Object).forEach(field => {
                        if (!nonConvertedGroups.includes(field)) {
                            // (group as Object)[field] = Boolean(group[field]);
                        }
                    });
                    return group as IGroup;
                });
            })
        );
    }

    createGroup$(data: IGroup): Observable<unknown> {
        return this.request$('createGroup', data).pipe(

        );
    }

    updateGroup$(data: IGroup): Observable<unknown> {
        return this.request$('updateGroup', data).pipe(

        );
    }

    delGroup$(group_id: number): Observable<unknown> {
        return this.request$('delGroup', {group_id}).pipe(

        );
    }

    addUsersToGroup$(group_id: number, users: number[]): Observable<unknown> {
        return this.request$('addUsersToGroup', {group_id, users}).pipe(

        );
    }

    delUsersFromGroup$(group_id: number, users: number[]): Observable<unknown> {
        return this.request$('delUsersFromGroup', {group_id, users}).pipe(

        );
    }

    getUsersInGroup$(group_id: number): Observable<IGroupUsers> {
        return this.request$<IGroupUsers>('getUsersInGroup', {group_id});
    }

    getRegionSupportUsers$(region_id: number | null): Observable<IRegionSupportUser[]> {
        return this.request$<{ users: IRegionSupportUser[] }>('getRegionSupportUsers', {region_id}).pipe(
            pluck('users'),
        );
    }

    getSupportCandidateUsers$(): Observable<IUser[]> {
        return this.request$<{ users: IUser[] }>('getSupportCandidateUsers').pipe(
            pluck('users'),
        );
    }

    saveRegionSupportUsers$(region_id: number | null, add: number[], remove: number[]): Observable<unknown> {
        return this.request$('saveRegionSupportUsers', {region_id, add, remove});
    }

    searchBillingEntitiesByRegion$(
        region_id: number, search_for?: string, start_from?: number, record_count?: number,
    ): Observable<IBillingEntity[]> {
        return this.request$<{ billing_entities: IBillingEntity[] }>('getBillingEntities', {
            region_id, search_for, start_from, record_count,
        }).pipe(
            pluck('billing_entities'),
        );
    }

    assignRegionSupportToBillingEntities$(region_id: number, billing_entity_ids: number[]): Observable<unknown> {
        return this.request$('assignRegionSupportToBillingEntities', {region_id, billing_entity_ids});
    }

    getRegionSupportBillingEntities$(region_id: number): Observable<IBillingEntity[]> {
        return this.request$<{ billing_entities: IBillingEntity[] }>('getRegionSupportBillingEntities', {region_id}).pipe(
            pluck('billing_entities'),
        );
    }

    unassignRegionSupportBillingEntity$(region_id: number, billing_entity_id: number): Observable<unknown> {
        return this.request$('unassignRegionSupportBillingEntity', {region_id, billing_entity_id});
    }

    unassignRegionSupportBillingEntities$(region_id: number, billing_entity_ids: number[]): Observable<unknown> {
        return this.request$('unassignRegionSupportBillingEntities', {region_id, billing_entity_ids});
    }

    searchSupportGroupsByName$(
        name_filter: string, start_from?: number, record_count?: number, group_types?: SupportGroupType[],
    ): Observable<ISupportGroupSearchRow[]> {
        return this.request$<{ groups: ISupportGroupSearchRow[] }>('searchSupportGroups', {
            name_filter, start_from, record_count, group_types,
        }).pipe(
            pluck('groups'),
        );
    }

    // Step 1 of the delete confirmation flow - never deletes anything itself. target_count is
    // passed through only for the server's audit log.
    verifySupportGroupDeleteIdentity$(confirmed_email: string, target_count: number): Observable<boolean> {
        return this.request$<{ match: boolean }>('verifySupportGroupDeleteIdentity', {
            confirmed_email, target_count,
        }).pipe(
            pluck('match'),
        );
    }

    bulkDeleteSupportGroups$(confirmed_email: string, targets: ISupportGroupDeleteTarget[]): Observable<ISupportGroupDeleteResult[]> {
        return this.request$<{ results: ISupportGroupDeleteResult[] }>('bulkDeleteSupportGroups', {confirmed_email, targets}).pipe(
            pluck('results'),
        );
    }

    addBillingEntityCommonExclusionDates$(billing_entity_id: number, exclusion_dates: string[]): Observable<unknown> {
        return ((exclusion_dates.length)) ? this.request$('addBillingEntityCommonExclusionDates', {
            billing_entity_id,
            exclusion_dates
        }) : of(false);
    }

    delBillingEntityCommonExclusionDates$(billing_entity_id: number, exclusion_dates: string[]): Observable<unknown> {
        return (exclusion_dates.length) ? this.request$('delBillingEntityCommonExclusionDates', {
            billing_entity_id,
            exclusion_dates
        }) : of(false);
    }

    getAIMLegalEntities$(region_id?: number): Observable<IAIMLegalEntity[]> {
        return this.request$<{ legal_entities: IAIMLegalEntity[] }>('getAIMLegalEntities', {region_id}).pipe(
            pluck('legal_entities')
        );
    }

    createAIMLegalEntity(name: string, region_id: number, default_for_region?: boolean): Observable<unknown> {
        return this.request$('createAIMLegalEntity', {
            name,
            region_id,
            default_for_region: Number(default_for_region)
        });
    }

    updateAIMLegalEntity$(newData: IAIMLegalEntity): Observable<unknown> {
        Object.assign(newData, {default_for_region: Number(newData.default_for_region)});
        return this.request$('updateAIMLegalEntity', newData);
    }

    delAIMLegalEntity$(aim_legal_entity_id: number): Observable<unknown> {
        return this.request$('delAIMLegalEntity', {aim_legal_entity_id});
    }

    getConfigs$(): Observable<IConfig[]> {
        return this.request$<IConfig[]>('getConfigs');
    }

    createConfig$(config_name: string, config_value: IConfigValue): Observable<unknown> {
        return this.request$('createConfig', {config_name, config_value});
    }

    updateConfig$(config_id: number, config_value: IConfigValue): Observable<unknown> {
        return this.request$('updateConfig', {config_id, config_value});
    }

    delConfig$(config_id: number): Observable<unknown> {
        return this.request$('delConfig', {config_id});
    }

    public getProducts$(work_zone_id?: number): Observable<IProduct[]> {
        return this.request$<{ products: IProduct[] }>('getProducts', (work_zone_id) ? {work_zone_id} : {}).pipe(
            pluck('products')
        );
    }

    public createProduct$(sku: string, description: string, addOn: boolean, work_zone_id: number): Observable<unknown> {
        return this.request$('createProduct', {sku, description, add_on: Number(addOn), work_zone_id});
    }

    public updateProduct$(data: Partial<IProduct>): Observable<unknown> {
        return this.request$('updateProduct', Object.assign({}, data, {add_on: Number(data.add_on)}));
    }

    public delProduct$(product_id: number): Observable<unknown> {
        return this.request$('delProduct', {product_id});
    }

    public createParam$(product_id: number, param_name: string, description: string): Observable<unknown> {
        return this.request$('createParam', {product_id, param_name, description});
    }

    public getProductParams$(product_id: number): Observable<IProductParam[]> {
        return this.request$<{ params: IProductParam[] }>('getParams', {product_id}).pipe(
            pluck('params')
        );
    }

    public updateParam$(data: IProductParam): Observable<unknown> {
        return this.request$('updateParam', data);
    }

    public delParam$(product_param_id: number): Observable<unknown> {
        return this.request$('delParam', {product_param_id});
    }

    public createParamValue$(product_param_id: number, value: string, order_num: number, hint?: string): Observable<unknown> {
        return this.request$('createParamValue', {product_param_id, value, order_num, hint});
    }

    public getParamValues$(product_param_id: number): Observable<IProductParamValue[]> {
        return this.request$<{ param_values: IProductParamValue[] }>('getParamValues', {product_param_id}).pipe(
            pluck('param_values')
        );
    }

    public updateParamValue$(data: Partial<IProductParamValue>): Observable<unknown> {
        return this.request$('updateParamValue', data);
    }

    public delParamValue$(param_value_id: number): Observable<unknown> {
        return this.request$('delParamValue', {param_value_id});
    }

    public getProductRegionalPrices$(product_id: number): Observable<IRegionalProductPrice[]> {
        return this.request$<{ regional_prices: IRegionalProductPrice[] }>('getSkuRegionalPrices', {product_id}).pipe(
            pluck('regional_prices')
        );
    }

    public setProductRegionalPrice$(product_id: string, region_id: string, currency: string, hourly_rate: number, fixed_price: number, min_minutes: number, billing_increment_mins: number): Observable<unknown> {
        return this.request$('setSkuRegionalPrice', {
            product_id,
            region_id,
            currency,
            hourly_rate,
            fixed_price,
            min_minutes,
            billing_increment_mins
        });
    }

    public delProductRegionalPrice$(sku_regional_price_id: string) {
        return this.request$('delSkuRegionalPrice', {sku_regional_price_id});
    }

    public getSkuBillingEntityPrices$(product_id: number): Observable<IBillingEntityProductPrice[]> {
        return this.request$<{ products: IBillingEntityProductPrice[] }>('getSkuBillingEntityPrices', {product_id}).pipe(
            pluck('products')
        );
    }


    public setSkuBillingEntityPrice$(product_id: string, billing_entity_id: number, currency: string, hourly_rate: number, fixed_price: number, min_minutes: number, billing_increment_mins: number): Observable<unknown> {
        return this.request$('setSkuBillingEntityPrice', {
            product_id,
            billing_entity_id,
            currency,
            hourly_rate,
            fixed_price,
            min_minutes,
            billing_increment_mins
        });
    }

    public delSkuBillingEntityPrice$(sku_billing_entity_price_id: number): Observable<unknown> {
        return this.request$('delSkuBillingEntityPrice', {sku_billing_entity_price_id});
    }

    private request$<T>(msg: string, data?: any): Observable<T> {
        return this.messages.request$(msg, data).pipe(
            catchError((error) => {
                this.errors.handleError(error, '@settings.service1');
                return EMPTY;
            })
        );
    }
}
